import api from './api';

export interface AndroidTvDevice {
  id: number;
  device_uid: string;
  nombre: string;
  ip: string;
  mac?: string | null;
  mdns_name?: string | null;
  habitacion_id: number | null;
  artefacto_id: number | null;
  artefacto?: { nombre?: string; icono?: string; color_icono?: string } | null;
  paired: boolean;
  online: boolean;
  is_on?: boolean | null;
  current_app?: string | null;
  volume?: number | null;
  mute?: boolean | null;
  enabled?: boolean;
  orden?: number;
  pin_required?: boolean;
  driver_config?: Record<string, unknown>;
  can_control?: boolean;
  can_view?: boolean;
}

export async function androidtvDiscover(): Promise<any> {
  const res = await api.post('/androidtv/discover');
  return res.data;
}

export async function androidtvAddByIp(ips: string[]): Promise<any> {
  const res = await api.post('/androidtv/add-by-ip', { ips });
  return res.data;
}

export async function fetchAndroidTvAdminSummary(): Promise<{
  total: number;
  unassigned_count: number;
  unpaired_count: number;
}> {
  const res = await api.get('/androidtv/devices/admin-summary');
  return res.data;
}

export async function fetchAndroidTvDevices(): Promise<AndroidTvDevice[]> {
  const res = await api.get('/androidtv/devices');
  return res.data || [];
}

export async function patchAndroidTvDevice(
  id: number,
  body: Record<string, unknown>,
): Promise<AndroidTvDevice> {
  const res = await api.patch(`/androidtv/devices/${id}`, body);
  return res.data;
}

export async function deleteAndroidTvDevice(id: number): Promise<void> {
  await api.delete(`/androidtv/devices/${id}`);
}

export async function reorderAndroidTvDevices(
  items: { id: number; orden: number }[],
): Promise<void> {
  await api.put('/androidtv/devices/reorder', { items });
}

export async function androidtvPairStart(id: number): Promise<any> {
  const res = await api.post(`/androidtv/devices/${id}/pair/start`);
  return res.data;
}

export async function androidtvPairFinish(id: number, pin: string): Promise<any> {
  const res = await api.post(`/androidtv/devices/${id}/pair/finish`, { pin });
  return res.data;
}

export async function androidtvControl(
  device_id: number,
  action: string,
  value?: unknown,
): Promise<any> {
  const res = await api.post('/androidtv/control', { device_id, action, value });
  return res.data;
}

export async function androidtvRefreshDevice(device_id: number): Promise<AndroidTvDevice> {
  const res = await api.post(`/androidtv/devices/${device_id}/refresh`);
  return res.data;
}

/** Renueva TTL de foco sin snapshot LAN (habitación visible → poll agresivo backend). */
export async function androidtvTouchFocus(deviceIds: number[]): Promise<void> {
  if (!deviceIds.length) return;
  await api.post('/androidtv/focus', { device_ids: deviceIds });
}

export interface AndroidTvActivityPoint {
  time_bucket: string;
  activity_ratio: number;
  temperatura_promedio: number | null;
}

export async function fetchAndroidTvActivitySeries(params: {
  deviceId: number;
  startDate: string;
  endDate: string;
  interval: string;
}): Promise<{ ok: boolean; data: AndroidTvActivityPoint[] }> {
  const res = await api.get('/androidtv/historical/activity-time-series', {
    params: {
      device_id: params.deviceId,
      start_date: params.startDate,
      end_date: params.endDate,
      interval: params.interval,
    },
  });
  return res.data;
}

// ----------------- Política IA -----------------

export interface AndroidTvPolicy {
  whitelist: boolean;
  blacklist: boolean;
  require_confirmation: boolean;
  defined?: boolean;
  notes?: string | null;
}

export interface AndroidTvPolicyRow {
  androidtv_device_id: number;
  whitelist: boolean;
  blacklist: boolean;
  require_confirmation: boolean;
  device_nombre?: string | null;
  habitacion_id?: number | null;
}

export async function getAndroidTvPolicy(deviceId: number): Promise<AndroidTvPolicy | null> {
  const res = await api.get<AndroidTvPolicy>(`/androidtv/devices/${deviceId}/ai-policy`);
  const p = res.data;
  if (!p || !p.defined) return null;
  return p;
}

export async function upsertAndroidTvPolicy(
  deviceId: number,
  input: {
    whitelist: boolean;
    blacklist: boolean;
    require_confirmation: boolean;
  },
): Promise<AndroidTvPolicy> {
  const res = await api.put<AndroidTvPolicy>(`/androidtv/devices/${deviceId}/ai-policy`, input);
  return res.data;
}

export async function deleteAndroidTvPolicy(deviceId: number): Promise<boolean> {
  await api.delete(`/androidtv/devices/${deviceId}/ai-policy`);
  return true;
}

export async function listAndroidTvPolicies(): Promise<AndroidTvPolicyRow[]> {
  const res = await api.get<{ policies: AndroidTvPolicyRow[] }>('/androidtv/ai-policies');
  return res.data.policies || [];
}
